/**
 * One particle: the values ParticleContainer steps each frame and the quad
 * that shows them, a Pixi Particle in the group for its blend mode. Particles
 * and their quads are pooled, so reset puts every field and every quad
 * attribute back for the next use.
 */
import type { Particle as PixiParticle, ParticleContainer as PixiParticleContainer, Texture } from 'pixi.js';
import Vector from '../Vector';
import { type ParticleTypeId, particleTypeIdByName } from './ParticleTypes';

/** A tint in the quad colour's byte order, blue high; the alpha byte goes on top of it. */
export function rgbToBgr(rgb: number): number {
  return ((rgb & 0xff) << 16) | (rgb & 0xff00) | ((rgb >>> 16) & 0xff);
}

export class Particle {
  /** Set by reset. */
  type: ParticleTypeId = particleTypeIdByName.MISSILE;
  readonly view: PixiParticle;
  /** The Pixi container that draws the quad: the group for the particle's blend mode. */
  group: PixiParticleContainer;
  readonly speed = new Vector(0, 0);
  readonly pos = new Vector(0, 0);
  readonly scale = new Vector(1, 1);
  alpha = 1;
  /** Radians per frame. */
  rotationSpeed = 0;
  /** Radians. */
  rotation = 0;
  /** The rotation last written to the quad; changes under 0.03 are skipped. */
  lastRotation = 0;
  /** The tint a missile trail fades from, or undefined for white. */
  tint: number | undefined;
  /** The current tint in the quad colour's byte order; the alpha byte is packed over it each frame. */
  colorBgr = 0xffffff;
  /** 0 to 1; the particle dies past 1. */
  life = 0;
  /** The size a missile trail or fragment puff grows towards. */
  scaleFactor = 0;
  /** The previous particle in spawn order, -1 at the head. */
  prev = -1;
  /** The next particle in spawn order, -1 at the tail. */
  next = -1;

  constructor(view: PixiParticle, group: PixiParticleContainer) {
    this.view = view;
    this.group = group;
  }

  /** Puts every field and quad attribute back for a pooled reuse; the vectors are set in place. */
  reset(
    type: ParticleTypeId,
    texture: Texture,
    group: PixiParticleContainer,
    speedX: number,
    speedY: number,
    posX: number,
    posY: number,
    scaleX: number,
    scaleY: number,
    alpha: number,
    rotationSpeed: number,
    rotation: number,
    tint: number | undefined,
    life: number | undefined,
    scaleFactor: number | undefined,
  ) {
    this.type = type;
    this.group = group;
    this.speed.set(speedX, speedY);
    this.pos.set(posX, posY);
    this.scale.set(scaleX, scaleY);
    this.alpha = alpha;
    this.rotationSpeed = rotationSpeed;
    this.rotation = rotation;
    this.lastRotation = 0;
    this.tint = tint;
    this.colorBgr = rgbToBgr(tint ?? 0xffffff);
    this.life = life ?? 0;
    this.scaleFactor = scaleFactor ?? 0;
    this.prev = -1;
    this.next = -1;

    // The quad starts where a fresh sprite did: at the origin, unit scale, unrotated, opaque in its tint
    const v = this.view;
    v.texture = texture;
    v.x = 0;
    v.y = 0;
    v.scaleX = 1;
    v.scaleY = 1;
    v.rotation = 0;
    v.color = this.colorBgr + (255 << 24);
  }
}
